// WHAT: React hook for fetching and aggregating dividend transaction data from Supabase
// WHY:  Provides state management for dividend transactions, summaries, and total calculations with performance optimization
// DOMAIN: Investments → Parqet → Dividend Tracking
// DEPENDENCIES: Supabase client, React hooks, database queries
// RISKS:
//   - NOTE: Uses two separate queries (total + limited list) - may cause data inconsistency if data changes between queries
//   - WARN: Summary calculation uses all data but transaction list is limited to 50 - may cause confusion
//   - NOTE: Date parsing assumes ISO format - may fail with malformed dates
//   - NOTE: Currency handling assumes single currency per transaction - may not handle multi-currency portfolios correctly

import { useEffect, useState, useCallback } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";
import { getSupabaseClient } from "@/shared/services";

export interface DividendTransaction {
  broker: null | string;
  currency: string;
  dividend_per_share: number;
  dividend_type: null | string;
  ex_date: null | string;
  id: string;
  net_dividend: number;
  payment_date: null | string;
  pdf_filename: null | string;
  security_id: string;
  shares: number;
  transaction_date: string;
}

export interface DividendSummary {
  currency: string;
  month: number;
  total_dividends: number;
  transaction_count: number;
  year: number;
}

interface DividendTotalRow {
  currency: null | string;
  net_dividend: null | number;
  transaction_date: string;
}

// NOTE: Calculate total dividends from all rows
const calculateTotalDividends = (rows: DividendTotalRow[]): number => {
  let total = 0;
  for (const tx of rows) {
    const amount = typeof tx.net_dividend === "number" ? tx.net_dividend : 0;
    total += amount;
  }
  return total;
};

// NOTE: Build monthly summary from all dividend transactions
const buildMonthlySummary = (rows: DividendTotalRow[]): DividendSummary[] => {
  const summaryMap = new Map<string, DividendSummary>();

  for (const tx of rows) {
    const date = new Date(tx.transaction_date);
    const year = date.getFullYear();
    // NOTE: getMonth() returns 0-11, so add 1 for human-readable month (1-12)
    const month = date.getMonth() + 1;
    // NOTE: Use year-month as key for grouping (e.g., "2024-7")
    const key = `${String(year)}-${String(month)}`;

    const existing = summaryMap.get(key);
    if (existing) {
      // NOTE: Aggregate dividends and count for existing month
      existing.total_dividends += typeof tx.net_dividend === "number" ? tx.net_dividend : 0;
      existing.transaction_count += 1;
    } else {
      // NOTE: Create new summary entry for month
      summaryMap.set(key, {
        currency: tx.currency ?? "EUR",
        month,
        total_dividends: typeof tx.net_dividend === "number" ? tx.net_dividend : 0,
        transaction_count: 1,
        year,
      });
    }
  }

  // NOTE: Convert map to array and sort by year (desc) then month (desc)
  const summaryValues: DividendSummary[] = [...summaryMap.values()];
  // Reason: toSorted returns array but TypeScript needs explicit type

  const sortedArray: unknown = summaryValues.toSorted((a: DividendSummary, b: DividendSummary) => {
    // NOTE: Sort by year first (newest first)
    if (a.year !== b.year) {
      return b.year - a.year;
    }
    // NOTE: Then sort by month (newest first)
    return b.month - a.month;
  });
  const sorted: DividendSummary[] = sortedArray as DividendSummary[];
  return sorted;
};

/**
 * Custom hook for fetching and aggregating dividend transaction data from Supabase
 *
 * @returns {object} Object containing transactions, summary, totalDividends, loading, error, and refetch function
 *
 */
export const useDividends = () => {
  const [transactions, setTransactions] = useState<DividendTransaction[]>([]);
  const [summary, setSummary] = useState<DividendSummary[]>([]);
  const [totalDividends, setTotalDividends] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  const fetchDividends = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const supabase = await getSupabaseClient();

      // NOTE: Load total sum of ALL dividends (without limit) for accurate total calculation
      // This query is separate from the transaction list to ensure accurate totals
      const { data: totalData, error: totalError } = await supabase
        .from("transactions_dividend")
        .select("net_dividend, currency, transaction_date")
        .order("transaction_date", { ascending: false });

      if (totalError) {
        throw new Error(totalError.message);
      }

      // NOTE: Explicitly type total rows to avoid `any` usage from Supabase client

      const totalRows = (totalData ?? []) as DividendTotalRow[];

      // NOTE: Calculate total from ALL dividends
      const total = calculateTotalDividends(totalRows);
      setTotalDividends(total);

      // NOTE: Load last 50 dividends for the list (with limit for performance)
      // This limits the amount of data transferred and rendered
      const { data: transactionsData, error: transactionsError } = await supabase
        .from("transactions_dividend")
        .select(
          `
          id,
          security_id,
          transaction_date,
          net_dividend,
          currency,
          shares,
          dividend_per_share,
          payment_date,
          ex_date,
          dividend_type,
          broker,
          pdf_filename
        `,
        )
        .order("transaction_date", { ascending: false })
        .limit(50); // NOTE: Limit to 50 for performance - summary uses all data

      if (transactionsError) {
        throw new Error(transactionsError.message);
      }

      setTransactions(transactionsData ?? []);

      // NOTE: Create summary by year/month from ALL dividends (not just last 50)
      const summaryArray = buildMonthlySummary(totalRows);
      setSummary(summaryArray);
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Failed to fetch dividends";
      setError(errorMessage);
      debugLog("error", "investments", "Error fetching dividends", {
        error: errorMessage,
        file: "useDividends.ts",
        function: "fetchDividends",
        module: "parqet",
      });
      setTransactions([]);
      setSummary([]);
      setTotalDividends(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchDividends();
  }, [fetchDividends]);

  return {
    error,
    loading,
    refetch: fetchDividends,
    summary,
    totalDividends,
    transactions,
  };
};
